import React from "react";
import { FormWrapper } from "./FormWrapper";
import { DropdownNode, UIPageType, uiTypes } from "./allData";
import { toast } from "@components/ui/use-toast";
import SettingsUIPage from "./SettingsUIPage";
import { Cog, CirclePlus, Trash, SquarePen } from "lucide-react";
import { Button } from "@components/ui/button";
import HelpDocumentDialog from "./HelpDocumentDialog";
import UiPreview from "./UiPreview";
import ActionsPage from "./ActionsPage";
import Confirmation from "@components/common/ConfirmDialog";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";
import { useMutation, useQuery } from "react-query";
import {
  fetchConfigPage,
  fetchUIPageData,
  saveUIPageInfo,
} from "./createPluginApi";
import PageLoader from "@components/loaders/PageLoader";
import SettingsInfo from "./SettingsInfo";
import FallBackUi from "./FallBackUi";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";

type UiData = {
  pluginName?: string;
  helpContent?: any;
  goToPage?: (index: number) => void;
  dropdownInput?: { label: string; value: string; apiEndpoint?: string };
  setDropdownInput?: React.Dispatch<
    React.SetStateAction<{ label: string; value: string; apiEndpoint?: string }>
  >;
  logicType?: any[];
  dropdownBlocks?: DropdownNode[];
  setDropdownBlocks?: any;
  // dropdownTrees?: DropdownNode[][];
  // setDropdownTrees?: React.Dispatch<React.SetStateAction<DropdownNode[][]>>;
  alldropdownTrees?: {
    dropdownTrees: DropdownNode[][];
    configDropdownTrees: DropdownNode[][];
  };
  setAlldropdownTrees?: React.Dispatch<
    React.SetStateAction<{
      dropdownTrees: DropdownNode[][];
      configDropdownTrees: DropdownNode[][];
    }>
  >;
};

type AddressFormProps = UiData & {
  updateUIPage: (page: UIPageType) => void;
};

export function UIPage({
  pluginName,
  goToPage,
  updateUIPage,
  dropdownInput,
  setDropdownInput,
  logicType,
  dropdownBlocks,
  setDropdownBlocks,
  alldropdownTrees,
  setAlldropdownTrees,
}: AddressFormProps) {
  const {
    pluginId,
    uiPages,
    selectedUiType,
    definedVariables,
    isSavedSection,
    fields,
    helpDocs,
    dispatch,
    hasUIPage,
    hasTempConfigPage,
  } = useMeshPluginContext();
  const [openAddHelpDocumentDialog, setOpenAddHelpDocumentDialog] =
    React.useState(false);
  const [openPreview, setOpenPreview] = React.useState(false);
  const [selectedDoc, setSelectedDoc] = React.useState<null | {
    title: string;
    content: string;
  }>(null);
  const [, setIsEditing] = React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [settingsConfirmationOpen, setSettingsConfirmationOpen] =
    React.useState(false);

  const [selectedDocIndex, setSelectedDocIndex] = React.useState<number | null>(
    null,
  );
  const [selectedSettingsIndex, setSelectedSettingsIndex] = React.useState<
    number | null
  >(null);
  const [dropdownSelections, setDropdownSelections] = React.useState<{
    [key: number]: string;
  }>({});
  const [dependentSelections, setDependentSelections] = React.useState<{
    [key: number]: string;
  }>({});
  const [selectedBundle, setSelectedBundle] = React.useState<string>("");
  const [selectedRequest, setSelectedRequest] = React.useState<string>("");

  const [submissionAction, setSubmissionAction] = React.useState({
    submitBundleId: "",
    submitRequest: "",
  });

  const [openNewSettingsDialog, setOpenNewSettingsDialog] =
    React.useState(false);
  const [newSettingsTitle, setNewSettingsTitle] = React.useState("");
  const [newSettingsDescription, setNewSettingsDescription] =
    React.useState("");
  const [isEditingSettings, setIsEditingSettings] = React.useState(false);
  const [editingSettingsIndex, setEditingSettingsIndex] = React.useState<
    number | null
  >(null);

  const isAlreadySavedUIPage =
    isSavedSection?.find((section) => section.name === "ui")?.isSaved ?? false;

  const cleanNullValues = React.useCallback((obj: any): any => {
    if (obj === null || obj === undefined) {
      return Array.isArray(obj) ? [] : obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(cleanNullValues);
    }

    if (typeof obj === "object") {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (key === "children" || key === "targetFields") {
          cleaned[key] = value === null ? [] : cleanNullValues(value);
        } else {
          cleaned[key] = cleanNullValues(value);
        }
      }
      return cleaned;
    }

    return obj;
  }, []);

  const extractActionFieldsFromPages = React.useCallback(
    (pages: any[] = []) => {
      const extractedFields: any[] = [];

      pages
        .filter((page: any) => page.pageType === "action")
        .forEach((page: any) => {
          (page.forms || []).forEach((form: any) => {
            (form.fieldList || []).forEach((field: any) => {
              const normalizedField: any = {
                fieldName: field.fieldTitle || "",
                fieldType: field.fieldType || "",
                defaultValue: field.default || "",
                placeholder: field.placeholder || "",
                infoIcon: field.infoIcon || "",
                dropdownOptions: (field.values || []).map((val: any) => ({
                  label: val.displayName || "",
                  value: val.value || "",
                })),
                dynamicRequest: field.dynamicRequest || "",
                isApiValue:
                  field.values?.some((val: any) => val.isDynamic === 1) ||
                  (field.dropdownTree &&
                    Array.isArray(field.dropdownTree) &&
                    field.dropdownTree.length > 0) ||
                  false,
                fromAnomaly: !!field.isAnomaly,
                isConfig: !!field.isConfig,
                onloadAction: field.onloadAction || null,
                bundleId: field.bundleId || null,
                requestId: field.requestId || null,
                userAction: field.userAction
                  ? {
                      trigger: field.userAction.trigger || "",
                      bundle: field.userAction.bundle || "",
                      bundleVariable: field.userAction.bundleVariable || "",
                      mapResponse: {
                        label: field.userAction.mapResponse?.label || "",
                        value: field.userAction.mapResponse?.value || "",
                      },
                      targetField: field.userAction.targetField || "",
                    }
                  : undefined,
              };

              if (
                field.dropdownTree &&
                Array.isArray(field.dropdownTree) &&
                field.dropdownTree.length > 0
              ) {
                normalizedField.dropdownTree = cleanNullValues(
                  field.dropdownTree,
                );
                normalizedField.isApiValue = true;
              }

              if (
                field.configDropdownTree &&
                Array.isArray(field.configDropdownTree) &&
                field.configDropdownTree.length > 0
              ) {
                normalizedField.configDropdownTree = cleanNullValues(
                  field.configDropdownTree,
                );
                normalizedField.isConfig = true;
              }

              if (field.fieldType === "submit") {
                normalizedField.submitConfig = {
                  Ismultiple: field.submitConfig?.Ismultiple || false,
                  bundleId: field.submitConfig?.bundleId || "",
                  requestId: field.submitConfig?.requestId || "",
                  submit_types: field.submitConfig?.submit_types || [],
                };
              }

              extractedFields.push(normalizedField);
            });
          });
        });

      return extractedFields;
    },
    [cleanNullValues],
  );

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  // Fetch existing UI Page data when pluginId changes
  const { data: fetchedUIPageData, isLoading: isFetchingUIPage } = useQuery(
    ["ui-page-data", pluginId],
    () => fetchUIPageData(pluginId!),
    {
      enabled: !!pluginId && isEditMode && hasUIPage,
    },
  );

  // Fetch config page data
  const { data: configPageData } = useQuery(
    ["configPage", pluginId, "temp"],
    () => fetchConfigPage(pluginId ?? "", "temp"),
    {
      enabled: !!pluginId && isEditMode && hasTempConfigPage,
    },
  );

  // to show the column name as value if its isDecript or similar
  const filterConfigKeys = (config: any) => {
    const excludeKeys = ["isDecript", "testConnectionBtn"];
    return Object.fromEntries(
      Object.entries(config).filter(([key]) => !excludeKeys.includes(key)),
    );
  };

  // Update savedSettings when configPageData changes
  React.useEffect(() => {
    if (!configPageData?.data) return;

    const newSavedSettings: Record<string, any[]> = {};
    const newConfigData: any[] = [];

    Object.entries(configPageData.data).forEach(([pageUid, configs]) => {
      const filteredConfigs = (configs as any[]).map(filterConfigKeys);
      newSavedSettings[pageUid] = filteredConfigs;
      newConfigData.push({ pageUid, data: filteredConfigs });
    });

    dispatch({
      type: MeshPluginActions.SET_SAVED_SETTINGS,
      payload: newSavedSettings,
    });
    dispatch({
      type: MeshPluginActions.SET_CONFIGURATION_DATA,
      payload: newConfigData,
    });
  }, [configPageData, dispatch]);

  // set ui data to state when fetchedUIPageData changes
  React.useEffect(() => {
    if (fetchedUIPageData?.data) {
      const pages = fetchedUIPageData.data;
      dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: pages });
      const extractedFields = extractActionFieldsFromPages(pages);

      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: extractedFields,
      });

      // Setup helpDocs only from help pages
      const helpDocsData = pages
        .filter((p: any) => p.pageType === "Help")
        .map((page: any) => ({
          title: page.pageTitle,
          content: page.helpContent || page.content || "",
        }));

      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: helpDocsData,
      });

      dispatch({
        type: MeshPluginActions.SET_IS_SAVED_SECTION,
        payload: (prev: { name: string; isSaved: boolean }[]) =>
          prev.map((section: { name: string; isSaved: boolean }) =>
            section.name === "ui" ? { ...section, isSaved: true } : section,
          ),
      });
    }
  }, [fetchedUIPageData?.data, dispatch, extractActionFieldsFromPages]);

  // Re-sync action fields whenever Action tab is selected so existing fields are restored reliably.
  React.useEffect(() => {
    if (selectedUiType !== "Action") return;
    if (!uiPages || uiPages.length === 0) return;

    const extractedFields = extractActionFieldsFromPages(uiPages as any[]);
    dispatch({
      type: MeshPluginActions.SET_ACTION_FIELDS,
      payload: extractedFields,
    });
  }, [selectedUiType, uiPages, dispatch, extractActionFieldsFromPages]);

  // Mutation for creating or updating UI Page
  const { mutate: createUIPage, isLoading: isSavingUIPage } = useMutation({
    mutationFn: saveUIPageInfo,
    onSuccess: () => {
      toast({
        variant: "success",
        title: `${isAlreadySavedUIPage ? "Updated" : "Created"}`,
        description: `UI Page ${isAlreadySavedUIPage ? "updated" : "created"} successfully.`,
      });
    },

    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Failed to create UI Page",
      });
    },
  });

  // Function to open new settings dialog
  const addNewSettingsPage = () => {
    setIsEditingSettings(false);
    setEditingSettingsIndex(null);
    setNewSettingsTitle("");
    setNewSettingsDescription("");
    setOpenNewSettingsDialog(true);
  };

  // Function to open edit settings dialog
  const handleEditSettingsClick = (index: number, page: UIPageType) => {
    setIsEditingSettings(true);
    setEditingSettingsIndex(index);
    setNewSettingsTitle(page.pageTitle || "");
    setNewSettingsDescription(page.description || "");
    setOpenNewSettingsDialog(true);
  };

  // Function to handle new settings submission
  const handleCreateNewSettings = () => {
    if (!newSettingsTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Settings title is required.",
      });
      return;
    }

    if (isEditingSettings && editingSettingsIndex !== null) {
      // Update existing settings
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: uiPages.map((page, idx) =>
          idx === editingSettingsIndex
            ? {
                ...page,
                pageTitle: newSettingsTitle.trim(),
                description: newSettingsDescription.trim(),
              }
            : page,
        ),
      });

      toast({
        variant: "success",
        title: "Settings Updated",
        description: "Settings page has been updated successfully.",
      });
    } else {
      // Create new settings
      const newUID = Date.now();
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: [
          ...uiPages,
          {
            uid: newUID,
            pageTitle: newSettingsTitle.trim(),
            pageType: "settings",
            infoIcon: "info",
            description: newSettingsDescription.trim(),
            forms: [],
          },
        ],
      });

      toast({
        variant: "success",
        title: "Settings Created",
        description: "New settings page has been created successfully.",
      });
    }

    // Reset and close dialog
    setNewSettingsTitle("");
    setNewSettingsDescription("");
    setOpenNewSettingsDialog(false);
    setIsEditingSettings(false);
    setEditingSettingsIndex(null);
  };

  // Function to delete a settings page
  const deleteSettingsPage = (index: number) => {
    setSelectedSettingsIndex(index);
    setSettingsConfirmationOpen(true);
  };

  // Confirm settings deletion
  const onDeleteSettings = () => {
    if (selectedSettingsIndex !== null && uiPages) {
      const updated = [...uiPages];
      if (
        selectedSettingsIndex >= 0 &&
        selectedSettingsIndex < updated.length
      ) {
        updated.splice(selectedSettingsIndex, 1);
        dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: updated });
        toast({
          variant: "success",
          title: "Deleted",
          description: "Settings page deleted successfully.",
        });
      }
      setSelectedSettingsIndex(null);
    }
    setSettingsConfirmationOpen(false);
  };

  // Close settings confirmation dialog
  const onCloseSettingsConfirmation = () => {
    setSettingsConfirmationOpen(false);
    setSelectedSettingsIndex(null);
  };

  // Function to update the page data in the state
  const updatePageData = (index: number, pageData: UIPageType) => {
    if (!uiPages) return;
    const updated = [...uiPages];
    updated[index] = pageData;
    dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: updated });
    updateUIPage(pageData);
  };

  // Function to handle saving help document content
  const handleSaveHelpContent = (
    doc: { title: string; content: string },
    isEditing: boolean,
  ) => {
    const trimmedTitle = doc.title.trim();
    const trimmedContent = doc.content.trim();

    if (!trimmedTitle || !trimmedContent || trimmedContent === "<p><br></p>") {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title and content cannot be empty.",
      });
      return;
    }

    const isDuplicate = helpDocs?.some(
      (d) =>
        d.title.trim().toLowerCase() === trimmedTitle.toLowerCase() &&
        (!isEditing || d.title.trim() !== selectedDoc?.title.trim()),
    );

    if (isDuplicate) {
      toast({
        variant: "destructive",
        title: "Duplicate Title",
        description: `A help document with the title "${trimmedTitle}" already exists.`,
      });
      return;
    }

    const newDoc = { title: trimmedTitle, content: trimmedContent };

    if (isEditing && selectedDoc) {
      // Update helpDocs
      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: (prev: { title: string; content: string }[]) =>
          prev.map((d: { title: string; content: string }) =>
            d.title === selectedDoc.title ? newDoc : d,
          ),
      });

      // Update uiPages
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: uiPages.map((page) =>
          page.pageType === "Help" && page.pageTitle === selectedDoc.title
            ? {
                ...page,
                pageTitle: newDoc.title,
                helpContent: newDoc.content,
              }
            : page,
        ),
      });

      toast({
        variant: "success",
        title: "Updated",
        description: `Help document "${trimmedTitle}" updated successfully.`,
      });
    } else {
      // Add to helpDocs
      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: (prev: { title: string; content: string }[]) => [
          ...prev,
          newDoc,
        ],
      });

      // Add to uiPages
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: [
          ...(uiPages ?? []),
          {
            pageTitle: newDoc.title,
            pageType: "Help",
            infoIcon: "info",
            description: "Help document",
            helpContent: newDoc.content,
            forms: [],
            uid: Date.now(),
          },
        ],
      });
    }

    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_HELP_DOCS,
      payload: true,
    });

    // Cleanup
    setOpenAddHelpDocumentDialog(false);
    setSelectedDoc(null);
    setIsEditing(false);
  };

  // UID generator
  let globalUIDCounter = Date.now();
  const generateUID = () => String(globalUIDCounter++);

  // Function to handle saving UI Pages
  const handleSaveSettings = (e?: React.MouseEvent) => {
    e?.preventDefault();

    if (!uiPages) return;

    // Validate helpDocs
    const helpTitles = new Set<string>();
    if (!helpDocs) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Help documents cannot be undefined.",
      });
      return;
    }

    for (const doc of helpDocs) {
      const title = doc.title.trim();
      const content = doc.content.trim();

      if (!title || !content || content === "<p><br></p>") {
        toast({
          variant: "destructive",
          title: "Error",
          description: `Help document "${title || "Untitled"}" is incomplete.`,
        });
        return;
      }

      const normalized = title.toLowerCase();
      if (helpTitles.has(normalized)) {
        toast({
          variant: "destructive",
          title: "Duplicate Help Title",
          description: `The help document title "${title}" is used more than once.`,
        });
        return;
      }

      helpTitles.add(normalized);
    }

    // Step 1: Filter and prepare base pages (settings only)
    const finalUIPages: any[] = uiPages
      ?.filter((page) => page.pageType === "settings")
      .map((page) => ({
        ...page,
        uid: page.uid ?? generateUID(),
        content: page.helpContent ?? "",
        forms: (page.forms ?? []).map((form) => ({
          ...form,
          uid: form.uid ?? generateUID(),
          userAction: form.userAction ? { ...form.userAction } : "",
          fieldList: form.fieldList.map((field) => ({
            ...field,
            uid: field.uid ?? generateUID(),
          })),
        })),
      }));

    //  ED: Define transformNode ONCE, outside the field loop
    const transformNode = (node: any): any => {
      const transformed: any = {
        label: node.label || "",
        value: node.value || "",
        target: node.target || "",
        bundleId: node.bundleId || "",
        variableName: node.variableName || "",
        labelKey: node.labelKey || "",
        valueKey: node.valueKey || "",
        availableKeys: Array.isArray(node.availableKeys)
          ? node.availableKeys
          : [],
        isDefaultOption: node.isDefaultOption || false,
      };

      //  Handle targetFields (for config dropdowns) - replace null with []
      if (node.targetFields && Array.isArray(node.targetFields)) {
        transformed.targetFields = node.targetFields.map((tf: any) => ({
          fieldName: tf.fieldName || "",
          givenName:
            tf.givenName ||
            (tf.fieldName
              ? tf.fieldName.toLowerCase().replace(/\s+/g, "_")
              : ""),
          sourceType: tf.sourceType || "bundle",
          bundleId: tf.bundleId || "",
          variableName: tf.variableName || "",
          labelKey: tf.labelKey || "",
          valueKey: tf.valueKey || "",
          availableKeys: tf.availableKeys || [],
          defaultOptions: tf.defaultOptions || [],
          selected_item: tf.selected_item || "", // Store selected data item for v2.0 workflows
          previewOptions:
            tf.previewOptions?.map((opt: any) => ({
              label: opt.label || "",
              value: opt.value || "",
              targetFields: (opt.targetFields || []).map((nestedTf: any) => ({
                ...nestedTf,
                fieldName: nestedTf.fieldName || "",
                givenName:
                  nestedTf.givenName ||
                  (nestedTf.fieldName
                    ? nestedTf.fieldName.toLowerCase().replace(/\s+/g, "_")
                    : ""),
              })),
              children: opt.children || [],
            })) || [],
          commonApiConfig: tf.commonApiConfig || undefined,
        }));
      } else {
        transformed.targetFields = [];
      }

      if (Array.isArray(node.children) && node.children.length > 0) {
        transformed.children = node.children.map(transformNode);
      } else {
        transformed.children = [];
      }

      return transformed;
    };

    // Step 2: Append action page from external `fields` if available
    if (fields && fields.length > 0) {
      const actionPageFromFields = {
        uid: generateUID(),
        pageTitle: "Action Page",
        pageType: "action",
        infoIcon: "",
        description: "Actions configured from fields",
        content: "",
        forms: [
          {
            formTitle: "Action Form",
            uid: generateUID(),
            userAction: {
              requestName: "",
              action: "",
              trigger: "",
            },
            fieldList: fields.map((field, index) => {
              const transformedField: any = {
                fieldId: index,
                fieldTitle: field.fieldName || "",
                givenName: field.fieldName || "",
                fieldType: field.fieldType || "",
                placeholder: field.placeholder || "",
                default: field.defaultValue || "",
                infoIcon: field.infoIcon || "",
                values: (field.dropdownOptions || []).map((opt) => ({
                  displayName: opt.label || "",
                  value: opt.value || "",
                  default: "",
                  isDynamic: field.isApiValue ? 1 : 0,
                  dynamiDataSource: [],
                })),
                onloadAction: field.onloadAction || null,
                userAction: field.userAction || {
                  trigger: "",
                  bundle: "",
                  bundleVariable: "",
                  mapResponse: {
                    label: "",
                    value: "",
                  },
                  targetField: "",
                },
                dynamicRequest: field.dynamicRequest || "",
                isAnomaly: !!field.fromAnomaly,
                isSettings: field.fieldType === "import_settings",
                isConfig: !!field.isConfig,
                submitConfig: field.submitConfig || null,
                bundleId: field.bundleId || null,
                requestId: field.requestId || null,
              };

              //  Handle dropdownTree if isApiValue is true
              if (
                field.isApiValue &&
                field.dropdownTree &&
                Array.isArray(field.dropdownTree) &&
                field.dropdownTree.length > 0
              ) {
                transformedField.dropdownTree =
                  field.dropdownTree.map(transformNode);
              } else {
                transformedField.dropdownTree = null;
              }

              //  Handle configDropdownTree if isConfig is true
              if (
                field.isConfig &&
                field.configDropdownTree &&
                Array.isArray(field.configDropdownTree) &&
                field.configDropdownTree.length > 0
              ) {
                transformedField.configDropdownTree =
                  field.configDropdownTree.map(transformNode);
              } else {
                transformedField.configDropdownTree = null;
              }

              // Override with actual userAction if exists
              if (
                field.userAction &&
                field.userAction.trigger &&
                field.userAction.bundle &&
                field.userAction.bundleVariable &&
                field.userAction.mapResponse &&
                field.userAction.mapResponse.label &&
                field.userAction.mapResponse.value &&
                field.userAction.targetField
              ) {
                transformedField.userAction = {
                  trigger: field.userAction.trigger,
                  bundle: field.userAction.bundle,
                  bundleVariable: field.userAction.bundleVariable,
                  mapResponse: {
                    label: field.userAction.mapResponse.label,
                    value: field.userAction.mapResponse.value,
                  },
                  targetField: field.userAction.targetField,
                };
              }

              // Set isAnomaly if needed
              if (field.fromAnomaly) {
                transformedField.isAnomaly = true;
              }

              // Set isSettings if needed
              if (field.fieldType === "import_settings") {
                transformedField.isSettings = true;
              }

              //  FIX: Add submitConfig for submit button - EXTRACT PROPERLY
              if (field.fieldType === "submit") {
                const isMultiple = field.submitConfig?.Ismultiple || false;
                transformedField.submitConfig = {
                  Ismultiple: isMultiple,
                  bundleId: isMultiple
                    ? ""
                    : field.submitConfig?.bundleId || "",
                  requestId: isMultiple
                    ? ""
                    : field.submitConfig?.requestId || "all_bundle_requests",
                  submit_types: field.submitConfig?.submit_types || [],
                };
              }

              //  Add bundleId and requestId for testConnectionBtn
              if ((field.fieldType as string) === "testConnectionBtn") {
                transformedField.bundleId =
                  field.bundleId || selectedBundle || "";
                transformedField.requestId =
                  field.requestId || selectedRequest || "";
                // Add workflow_id for mesh version 2.0
                if (field.bundleId && field.bundleId.startsWith("workflow_")) {
                  transformedField.workflow_id = field.bundleId;
                }
              }

              return transformedField;
            }),
          },
        ],
      };

      finalUIPages.push(actionPageFromFields);
    }

    // Step 3: Add help pages
    helpDocs.forEach((doc) => {
      finalUIPages.push({
        uid: generateUID(),
        pageTitle: doc.title.trim(),
        pageType: "Help",
        infoIcon: "info",
        description: "Help document",
        helpContent: doc.content.trim(),
        content: doc.content.trim(),
        forms: [],
      });
    });

    // Step 4: Final transformation before API
    const transformedPages = finalUIPages.map((page) => ({
      ...page,
      forms: (page.forms ?? []).map((form: any) => ({
        ...form,
        userAction:
          form.userAction && typeof form.userAction === "object"
            ? {
                requestName: form.userAction.requestName || "",
                action: form.userAction.action || "",
                trigger: (form.userAction as any).trigger || "",
              }
            : {
                requestName: "",
                action: "",
                trigger: "",
              },
        fieldList: form.fieldList.map((field: any) => {
          const fieldObj: any = {
            fieldId: field.fieldId,
            fieldTitle: field.fieldTitle || "",
            givenName: field.givenName || "",
            fieldType: field.fieldType || "",
            placeholder: field.placeholder || "",
            default: field.default || "",
            infoIcon: field.infoIcon || "",
            values: (field.values || []).map((val: any) => ({
              displayName: val.displayName || "",
              value: val.value || "",
              default: val.default || "",
              isDynamic: val.isDynamic ?? 0,
              dynamiDataSource: val.dynamiDataSource || [],
            })),
            onloadAction: field.onloadAction
              ? {
                  requestName: field.onloadAction.requestName || "",
                }
              : undefined,
            userAction: {
              trigger: field.userAction?.trigger || "",
              bundle: field.userAction?.bundle || "",
              bundleVariable: field.userAction?.bundleVariable || "",
              mapResponse: {
                label: field.userAction?.mapResponse?.label || "",
                value: field.userAction?.mapResponse?.value || "",
              },
              targetField: field.userAction?.targetField || "",
            },
            isAnomaly: !!field.isAnomaly,
            isSettings: field.fieldType == "import_settings" ? true : false,
            isConfig: !!field.isConfig,
            dynamicRequest: field.dynamicRequest || "",
          };

          if (field.fromVariable) {
            fieldObj.fromVariable = true;
            fieldObj.sourceBundleId = field.sourceBundleId || "";
            fieldObj.selectedVariable = field.selectedVariable || "";
            fieldObj.labelKey = field.labelKey || "";
            fieldObj.valueKey = field.valueKey || "";
          }

          // Include dropdownTree if it exists
          if (
            field.dropdownTree &&
            Array.isArray(field.dropdownTree) &&
            field.dropdownTree.length > 0
          ) {
            fieldObj.dropdownTree = field.dropdownTree.map((node: any) => ({
              ...node,
              targetFields: (node.targetFields || []).map((tf: any) => ({
                ...tf,
                fieldName: tf.fieldName || "",
                givenName:
                  tf.givenName ||
                  (tf.fieldName
                    ? tf.fieldName.toLowerCase().replace(/\s+/g, "_")
                    : ""),
                selected_item: tf.selected_item || "", // Store selected data item for v2.0
                previewOptions: (tf.previewOptions || []).map((opt: any) => ({
                  ...opt,
                  targetFields: (opt.targetFields || []).map(
                    (nestedTf: any) => ({
                      ...nestedTf,
                      fieldName: nestedTf.fieldName || "",
                      givenName:
                        nestedTf.givenName ||
                        (nestedTf.fieldName
                          ? nestedTf.fieldName
                              .toLowerCase()
                              .replace(/\s+/g, "_")
                          : ""),
                      selected_item: nestedTf.selected_item || "", // Store for nested fields
                    }),
                  ),
                })),
              })),
            }));
          }

          //  Include configDropdownTree if it exists
          if (
            field.configDropdownTree &&
            Array.isArray(field.configDropdownTree) &&
            field.configDropdownTree.length > 0
          ) {
            fieldObj.configDropdownTree = field.configDropdownTree.map(
              (node: any) => ({
                ...node,
                targetFields: (node.targetFields || []).map((tf: any) => ({
                  ...tf,
                  fieldName: tf.fieldName || "",
                  givenName:
                    tf.givenName ||
                    (tf.fieldName
                      ? tf.fieldName.toLowerCase().replace(/\s+/g, "_")
                      : ""),
                  selected_item: tf.selected_item || "", // Store selected data item for v2.0
                  previewOptions: (tf.previewOptions || []).map((opt: any) => ({
                    ...opt,
                    targetFields: (opt.targetFields || []).map(
                      (nestedTf: any) => ({
                        ...nestedTf,
                        fieldName: nestedTf.fieldName || "",
                        givenName:
                          nestedTf.givenName ||
                          (nestedTf.fieldName
                            ? nestedTf.fieldName
                                .toLowerCase()
                                .replace(/\s+/g, "_")
                            : ""),
                        selected_item: nestedTf.selected_item || "", // Store for nested fields
                      }),
                    ),
                  })),
                })),
              }),
            );
          }

          //  Include submitConfig as nested object for submit buttons
          if (field.fieldType === "submit") {
            const isMultiple = field.submitConfig?.Ismultiple || false;
            const bundleId = isMultiple
              ? ""
              : field.submitConfig?.bundleId ||
                field.bundleId ||
                selectedBundle ||
                "";
            const requestId = isMultiple
              ? ""
              : field.submitConfig?.requestId ||
                field.requestId ||
                selectedRequest ||
                "all_bundle_requests";

            fieldObj.submitConfig = {
              Ismultiple: isMultiple,
              bundleId: bundleId,
              requestId: requestId,
              submit_types: field.submitConfig?.submit_types || [],
            };
          }

          //  Include bundleId and requestId for testConnectionBtn
          if (field.fieldType === "testConnectionBtn") {
            fieldObj.bundleId = field.bundleId || selectedBundle || "";
            fieldObj.requestId = field.requestId || selectedRequest || "";
            // Add workflow_id for mesh version 2.0
            if (
              field.workflow_id ||
              (field.bundleId && field.bundleId.startsWith("workflow_"))
            ) {
              fieldObj.workflow_id = field.workflow_id || field.bundleId;
            }
          }

          return fieldObj;
        }),
      })),
    }));

    // Step 5: Call API
    createUIPage({
      plugin_id: pluginId || "",
      pages: transformedPages,
      is_edit: isAlreadySavedUIPage || false,
    });

    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_HELP_DOCS,
      payload: false,
    });
    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
      payload: false,
    });

    // Reset the trigger after a short delay
    dispatch({
      type: MeshPluginActions.SET_IS_SAVED_SECTION,
      payload: (prev: { name: string; isSaved: boolean }[]) =>
        prev.map((section: { name: string; isSaved: boolean }) =>
          section.name === "ui" ? { ...section, isSaved: true } : section,
        ),
    });
  };

  const handleDeleteHelpDocument = (index: number) => {
    setSelectedDocIndex(index);
    setConfirmationOpen(true);
  };

  // Handlers for confirmation dialog
  const onDelete = () => {
    if (selectedDocIndex !== null && helpDocs) {
      const docToDelete = helpDocs[selectedDocIndex];
      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: helpDocs.filter((_, idx) => idx !== selectedDocIndex),
      });
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: (uiPages ?? []).filter(
          (page) =>
            !(page.pageType === "Help" && page.pageTitle === docToDelete.title),
        ),
      });
      toast({
        variant: "success",
        title: "Deleted",
        description: `Help document "${docToDelete.title}" deleted successfully.`,
      });
      setSelectedDocIndex(null);
    }
    setConfirmationOpen(false);
  };

  // Close confirmation dialog
  const onClose = () => {
    setConfirmationOpen(false);
  };

  const getFirstLineFromHtml = (htmlContent: string): string => {
    if (!htmlContent) return "No description";

    const content = htmlContent.trim();

    //  Detect special content types first (faster, no DOM needed)
    if (/<img\b[^>]*>/i.test(content)) return "🖼️ Image ";
    if (/<iframe\b[^>]*>/i.test(content)) return "🎥 Video ";
    if (/<(ul|ol)\b[^>]*>/i.test(content)) return "📋 List ";
    if (/<table\b[^>]*>/i.test(content)) return "📊 Table ";
    if (/<a\b[^>]*href=/i.test(content)) return "🔗 Link ";

    //  Strip HTML safely
    const textContent = content
      .replace(/<[^>]+>/g, "") // remove tags
      .replace(/\s+/g, " ") // normalize whitespace
      .trim();

    if (!textContent) return "No description";

    //  Get first line or sentence
    const firstLine = textContent.split("\n")[0].trim();
    const firstSentence = textContent.split(". ")[0].trim();

    const result = firstLine || firstSentence;

    //  Truncate if too long
    return result.length > 100 ? result.substring(0, 100) + "..." : result;
  };
  return (
    <>
      <FormWrapper title="UI Pages" option="ui" goToPage={goToPage}>
        <PageLoader show={isSavingUIPage || isFetchingUIPage} />

        {openAddHelpDocumentDialog && (
          <HelpDocumentDialog
            open={openAddHelpDocumentDialog}
            handleClose={() => {
              setOpenAddHelpDocumentDialog(false);
              setSelectedDoc(null);
              setIsEditing(false);
            }}
            handleSave={handleSaveHelpContent}
            initialDoc={selectedDoc}
          />
        )}

        {confirmationOpen && (
          <Confirmation
            text={"Are you sure , you want to delete this Help document ?"}
            confirmAction={onDelete}
            rejectAction={onClose}
            confirmText="Yes, delete it!"
            rejectText="Cancel"
            show={confirmationOpen}
            closeDialog={onClose}
          />
        )}

        {settingsConfirmationOpen && (
          <Confirmation
            text={
              "Are you sure you want to delete this Settings page? All forms in this page will be deleted."
            }
            confirmAction={onDeleteSettings}
            rejectAction={onCloseSettingsConfirmation}
            confirmText="Yes, delete it!"
            rejectText="Cancel"
            show={settingsConfirmationOpen}
            closeDialog={onCloseSettingsConfirmation}
          />
        )}

        {openPreview && (
          <UiPreview
            pluginId={pluginId || ""}
            pluginName={pluginName}
            open={openPreview}
            setOpenPreview={setOpenPreview}
            pages={uiPages ?? []}
          />
        )}

        <SettingsInfo
          openNewSettingsDialog={openNewSettingsDialog}
          setOpenNewSettingsDialog={setOpenNewSettingsDialog}
          newSettingsTitle={newSettingsTitle}
          setNewSettingsTitle={setNewSettingsTitle}
          newSettingsDescription={newSettingsDescription}
          setNewSettingsDescription={setNewSettingsDescription}
          handleCreateNewSettings={handleCreateNewSettings}
          isEditingSettings={isEditingSettings}
          onCancel={() => {
            setOpenNewSettingsDialog(false);
            setNewSettingsTitle("");
            setNewSettingsDescription("");
            setIsEditingSettings(false);
            setEditingSettingsIndex(null);
          }}
        />

        <div className="h-full min-h-0 flex flex-col">
          {/* UI type dropdown */}
          <div className="flex justify-between items-end w-full gap-1 mb-4 shrink-0">
            <div className="w-full">
              <Tabs
                value={selectedUiType || ""}
                onValueChange={(value) =>
                  dispatch({
                    type: MeshPluginActions.SET_SELECTED_UI_TYPE,
                    payload: value,
                  })
                }
                className="w-full"
              >
                <TabsList className="bg-background rounded-2xl w-fit flex justify-start border-b pb-1 h-full">
                  {uiTypes?.map((type) => (
                    <TabsTrigger
                      key={type.label}
                      value={type.label}
                      className="data-[state=active]:bg-primary data-[state=active]:text-background rounded-xl px-4 py-2 text-sm min-w-[120px]"
                    >
                      {type.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {selectedUiType === "Settings" && (
              <>
                <div
                  className="group mr-3 text-sm flex items-center border border-secondary px-4 py-2 rounded-2xl gap-1 cursor-pointer"
                  onClick={() => {
                    const hasValidSettings = uiPages?.some(
                      (page) =>
                        page.pageType === "settings" &&
                        page.forms &&
                        page.forms.length > 0,
                    );
                    const hasHelpContent = helpDocs && helpDocs.length > 0;

                    if (!hasValidSettings && !hasHelpContent) {
                      toast({
                        variant: "destructive",
                        title: "Nothing to preview",
                        description:
                          "Add at least one form or help document to preview.",
                      });
                      return;
                    }

                    setOpenPreview(true);
                  }}
                >
                  <Cog
                    size={20}
                    className="transition-transform duration-700 group-hover:rotate-180 group-hover:text-primary"
                  />
                  <span className="group-hover:text-primary text-md">
                    {" "}
                    Preview{" "}
                  </span>
                  {/* show save only when settings tab is active */}
                </div>
              </>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            {/* Render content based on selected UI type */}
            {selectedUiType === "Settings" && (
              <>
                {
                  <div className="border border-secondary rounded-2xl p-5 ">
                    <div className="flex w-full justify-between items-center border-b border-secondary pb-2">
                      <p className="text-sm  ">Help Documents </p>

                      <div
                        className="text-primaryBlue flex items-center gap-1 text-sm w-fit cursor-pointer hover:text-muted-foreground transition-colors"
                        onClick={() => setOpenAddHelpDocumentDialog(true)}
                      >
                        <CirclePlus size={15} /> Add Help Document
                      </div>
                    </div>

                    <div className="flex gap-4 flex-wrap mt-5">
                      {helpDocs && helpDocs.length > 0 ? (
                        helpDocs.map((doc, idx) => (
                          <div
                            key={idx}
                            className="min-w-[220px] group border border-lightBlue flex items-center justify-between gap-2 py-4 px-4 shadow-lg rounded-xl w-full sm:w-auto bg-muted transition-colors cursor-pointer border-l-[5px] border-l-primaryBlue hover:text-foreground"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setIsEditing(true);
                              setOpenAddHelpDocumentDialog(true);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              {/* <Book size={14} className="text-primary" /> */}
                              <span className="text-[22px]">📄</span>
                              <div className="flex-1 overflow-hidden">
                                <p className="text-foreground text-sm font-medium">
                                  {doc.title}
                                </p>
                                <p className="text-[10px] text-muted-foreground truncate max-w-[130px]">
                                  {getFirstLineFromHtml(doc.content)}
                                </p>
                              </div>
                            </div>

                            <span
                              className="opacity-0 group-hover:opacity-100 transition-opacity  hover:text-primary "
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteHelpDocument(idx);
                              }}
                            >
                              <Trash size={14} />
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="w-full items-center">
                          <FallBackUi
                            title="No Help documents added yet !"
                            description="Click 'Add Help Document' to create a help document."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                }

                {/* Settings section */}

                <div className=" border border-secondary rounded-2xl pt-5 pl-5 pr-5 pb-0 relative mt-4 mb-4">
                  <div className="flex justify-between items-center border-b border-secondary pb-2 mb-4">
                    <p className="text-sm">Settings Page </p>

                    {/* Add New Settings Button for one settings only for now */}
                    {uiPages?.filter((page) => page.pageType === "settings")
                      .length == 0 && (
                      <div
                        className="flex items-center gap-1 text-sm text-primaryBlue cursor-pointer hover:text-muted-foreground transition-colors"
                        onClick={addNewSettingsPage}
                      >
                        <CirclePlus size={15} /> Add New Settings
                      </div>
                    )}
                  </div>

                  <div className="overflow-y-auto flex flex-wrap gap-2  max-h-[250px] pb-5 px-1">
                    {(() => {
                      // Filter out empty settings pages (no title and no forms)
                      const validSettingsPages =
                        uiPages?.filter(
                          (page) =>
                            page.pageType === "settings" &&
                            (page.pageTitle?.trim() ||
                              (page.forms && page.forms.length > 0)),
                        ) || [];

                      return validSettingsPages.length > 0 ? (
                        validSettingsPages.map((page) => {
                          const actualIndex = uiPages!.findIndex(
                            (p) => p.uid === page.uid,
                          );

                          return (
                            <div
                              key={page.uid}
                              className="group relative w-[49%] rounded-xl shadow-lg border border-lightBlue 
                          bg-muted p-4 pb-5
                          transition-all duration-300
                          hover:-translate-y-1.1
                          hover:shadow-[0_5px_10px_rgba(0,0,0,0.2)]"
                            >
                              <div className="flex w-full justify-between items-center border-b cursor-default">
                                {/* <p className="text-sm font-semibold mb-1 text-gray-400">
                              Settings #{idx + 1}
                            </p> */}
                                {page.pageTitle && (
                                  <p className="text-md text-primaryBlue font-semibold capitalize px-1">
                                    {page.pageTitle}
                                  </p>
                                )}
                                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity pb-1">
                                  <div
                                    className="flex items-center justify-center p-1 bg-muted rounded-sm"
                                    title="Edit Settings"
                                  >
                                    <SquarePen
                                      onClick={() =>
                                        handleEditSettingsClick(
                                          actualIndex,
                                          page,
                                        )
                                      }
                                      size={14}
                                      className="text-muted-foreground cursor-pointer hover:text-foreground"
                                    />
                                  </div>
                                  <div
                                    className="flex items-center justify-center p-1 bg-muted rounded-sm"
                                    title="Delete Settings"
                                  >
                                    <Trash
                                      onClick={() =>
                                        deleteSettingsPage(actualIndex)
                                      }
                                      className="text-muted-foreground cursor-pointer hover:text-primary"
                                      size={14}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="flex justify-between items-center mb-3 cursor-default">
                                <div
                                  className="flex-1 mt-1"
                                  title={page.description}
                                >
                                  {page.description && (
                                    <p className="text-xs text-muted-foreground mt-1 max-w-[90%] max-h-[30px] overflow-hidden text-ellipsis px-1 ">
                                      {page.description}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <SettingsUIPage
                                pageData={page}
                                onSave={(data) =>
                                  updatePageData(actualIndex, data)
                                }
                                logicType={logicType}
                                definedVariables={definedVariables}
                                pluginId={pluginId}
                                setSelectedBundle={setSelectedBundle}
                                setSelectedRequest={setSelectedRequest}
                                selectedBundle={selectedBundle}
                                selectedRequest={selectedRequest}
                              />
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-muted-foreground text-sm">
                          <FallBackUi
                            title="No settings pages added yet."
                            description="Click 'Add New Settings' to create a new settings."
                          />
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            {selectedUiType == "Action" && (
              <div className="h-full min-h-[200px]">
                <ActionsPage
                  className="h-full"
                  dropdownInput={dropdownInput}
                  setDropdownInput={setDropdownInput}
                  dropdownSelections={dropdownSelections}
                  setDropdownSelections={setDropdownSelections}
                  dependentSelections={dependentSelections}
                  setDependentSelections={setDependentSelections}
                  definedVariables={definedVariables}
                  logicType={logicType}
                  dropdownBlocks={dropdownBlocks}
                  setDropdownBlocks={setDropdownBlocks}
                  alldropdownTrees={alldropdownTrees}
                  setAlldropdownTrees={setAlldropdownTrees}
                  handleSaveSettings={handleSaveSettings}
                  setDefinedVariables={(vars) =>
                    dispatch({
                      type: MeshPluginActions.SET_DEFINED_VARIABLES,
                      payload: vars,
                    })
                  }
                  submissionAction={submissionAction}
                  setSubmissionAction={setSubmissionAction}
                />
              </div>
            )}
          </div>

          {selectedUiType == "Settings" && (
            <div className="sticky !bottom-0 z-20 flex shrink-0 justify-end rounded-md border border-border/40 bg-background/30 py-2 shadow-sm backdrop-blur-md">
              <Button type="button" onClick={() => handleSaveSettings()}>
                Save Settings
              </Button>
            </div>
          )}
        </div>
      </FormWrapper>
    </>
  );
}
